import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, X } from "lucide-react";
import api, { fmtErr } from "../../lib/api";
import { PeriodPicker } from "./PeriodPicker";

const todayISO = () => new Date().toISOString().split("T")[0];
const eur = (v) => `${Number(v).toFixed(2)} €`;

const STATUS_ROW_COLORS = {
  Pago: "bg-emerald-100 border-l-4 border-l-emerald-600",
  Agendado: "bg-sky-100 border-l-4 border-l-sky-600",
  "Em curso": "bg-amber-100 border-l-4 border-l-amber-500",
  Reagendado: "bg-orange-100 border-l-4 border-l-orange-500",
};

const emptyForm = () => ({
  date: todayISO(),
  client: "",
  status: "Pago",
  service: "",
  product: "",
  team_member: "",
  amount: "",
  payment: "",
  obs: "",
  booking_id: "",
});

export const CashSheet = ({ menus, prefill }) => {
  const [range, setRange] = useState(null);
  const [sales, setSales] = useState([]);
  const [clients, setClients] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [servicePrices, setServicePrices] = useState({});

  const load = useCallback(() => {
    if (!range) return;
    api
      .get("/admin/sales", { params: { start: range.start, end: range.end } })
      .then((res) => setSales(res.data));
  }, [range]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    api.get("/admin/clients").then((res) => setClients(res.data));
  }, []);

  // Catálogo de preços dos serviços de marcação (para preenchimento automático do valor)
  useEffect(() => {
    api
      .get("/admin/booking-services")
      .then((res) => {
        const map = {};
        (res.data || []).forEach((s) => {
          if (s.price != null) map[s.name] = s.price;
        });
        setServicePrices(map);
      })
      .catch(() => {});
  }, []);

  // Ao escolher o serviço, preencher o valor automaticamente (mantém-se editável)
  const onServiceChange = (name) => {
    setForm((f) => {
      const next = { ...f, service: name };
      if (name && servicePrices[name] != null) {
        next.amount = String(servicePrices[name]);
      }
      return next;
    });
  };

  useEffect(() => {
    if (prefill && prefill._k) {
      setEditing(null);
      setForm({
        ...emptyForm(),
        client: prefill.client || "",
        service: prefill.service || "",
        team_member: prefill.team_member || "",
        amount: prefill.amount != null ? String(prefill.amount) : "",
        date: prefill.date || todayISO(),
        obs: prefill.obs || "",
        booking_id: prefill.booking_id || "",
      });
      setShowForm(true);
    }
  }, [prefill]);

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm());
    setShowForm(true);
  };

  const openEdit = (sale) => {
    setEditing(sale.id);
    setForm({ ...sale, product: sale.product || "", obs: sale.obs || "" });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.status === "Pago" && !form.payment) {
      toast.error("Selecione o método de pagamento para vendas pagas.");
      return;
    }
    setSaving(true);
    const payload = { ...form, amount: parseFloat(form.amount) };
    try {
      if (editing) {
        await api.put(`/admin/sales/${editing}`, payload);
        toast.success("Venda atualizada.");
      } else {
        await api.post("/admin/sales", payload);
        toast.success("Venda registada.");
      }
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar este registo?")) return;
    await api.delete(`/admin/sales/${id}`);
    toast.success("Registo eliminado.");
    load();
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/sales/${id}`, { status });
      toast.success(`Estado alterado para "${status}".`);
      load();
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail));
      load();
    }
  };

  const total = sales.reduce((sum, s) => sum + s.amount, 0);
  const inputCls =
    "w-full bg-white border border-black/15 px-3 py-2.5 text-sm font-light focus:outline-none focus:border-black";

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <PeriodPicker onRange={setRange} defaultMode="mes" testPrefix="cash-period" />
        <div className="flex items-center gap-6">
          <p data-testid="cash-total" className="font-display text-2xl tracking-tight">
            {eur(total)} <span className="text-sm text-black/40 font-sans font-light">({sales.length} registos)</span>
          </p>
          <button
            data-testid="cash-add-btn"
            onClick={openNew}
            className="label-editorial bg-black text-white px-6 py-3.5 hover:bg-black/80 transition-colors flex items-center gap-2"
          >
            <Plus size={14} /> Nova Venda
          </button>
        </div>
      </div>

      {showForm && (
        <div
          data-testid="sale-form-overlay"
          className="fixed inset-0 z-[80] flex items-start md:items-center justify-center p-4 bg-black/50 animate-fade-in overflow-y-auto"
          onClick={() => setShowForm(false)}
        >
          <form
            onSubmit={handleSubmit}
            data-testid="sale-form"
            onClick={(e) => e.stopPropagation()}
            className="bg-white border border-black/10 p-6 md:p-8 space-y-5 w-full max-w-3xl my-8 animate-pop-in shadow-xl"
          >
          <div className="flex items-center justify-between">
            <p className="label-editorial">{editing ? "Editar venda" : "Nova venda"}</p>
            <button type="button" onClick={() => setShowForm(false)} data-testid="sale-form-close">
              <X size={18} />
            </button>
          </div>
          {form.booking_id && (
            <div data-testid="sale-from-booking" className="bg-clay/10 border border-clay/30 text-clay-dark text-xs px-4 py-2.5 tracking-wide">
              A converter uma marcação em venda. Confirme os dados e guarde — a marcação ficará como <strong>concluída</strong>.
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="label-editorial text-black/50 block mb-2">Data</label>
              <input data-testid="sale-date-input" required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className="label-editorial text-black/50 block mb-2">Cliente</label>
              <input data-testid="sale-client-input" required list="clients-list" value={form.client} onChange={(e) => setForm({ ...form, client: e.target.value })} placeholder="Nome do cliente" className={inputCls} />
              <datalist id="clients-list">
                {clients.map((c) => <option key={c} value={c} />)}
              </datalist>
            </div>
            <div>
              <label className="label-editorial text-black/50 block mb-2">Estado</label>
              <select data-testid="sale-status-select" required value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputCls}>
                {menus.statuses.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="label-editorial text-black/50 block mb-2">Serviço</label>
              <select data-testid="sale-service-select" required value={form.service} onChange={(e) => onServiceChange(e.target.value)} className={inputCls}>
                <option value="">Escolher…</option>
                {menus.services.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="label-editorial text-black/50 block mb-2">Produto</label>
              <input data-testid="sale-product-input" value={form.product} onChange={(e) => setForm({ ...form, product: e.target.value })} placeholder="(opcional)" className={inputCls} />
            </div>
            <div>
              <label className="label-editorial text-black/50 block mb-2">Team Member</label>
              <select data-testid="sale-team-select" required value={form.team_member} onChange={(e) => setForm({ ...form, team_member: e.target.value })} className={inputCls}>
                <option value="">Escolher…</option>
                {menus.team_members.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="label-editorial text-black/50 block mb-2">Valor (€)</label>
              <input data-testid="sale-amount-input" required type="number" step="0.01" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0.00" className={inputCls} />
            </div>
            <div>
              <label className="label-editorial text-black/50 block mb-2">
                Pagamento{form.status === "Pago" ? " *" : ""}
              </label>
              <select data-testid="sale-payment-select" required={form.status === "Pago"} value={form.payment} onChange={(e) => setForm({ ...form, payment: e.target.value })} className={inputCls}>
                <option value="">Escolher…</option>
                {menus.payments.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="label-editorial text-black/50 block mb-2">Obs.</label>
            <input data-testid="sale-obs-input" value={form.obs} onChange={(e) => setForm({ ...form, obs: e.target.value })} placeholder="(opcional)" className={inputCls} />
          </div>
          <button data-testid="sale-submit-btn" type="submit" disabled={saving} className="label-editorial bg-black text-white px-8 py-3.5 hover:bg-black/80 transition-colors disabled:opacity-50">
            {saving ? "A guardar…" : editing ? "Guardar Alterações" : "Registar Venda"}
          </button>
          </form>
        </div>
      )}

      <div className="bg-white border border-black/5 overflow-x-auto">
        <table data-testid="sales-table" className="w-full text-sm font-light min-w-[900px]">
          <thead>
            <tr className="text-left text-xs uppercase tracking-widest text-black/40 border-b border-black/10">
              <th className="p-4">Data</th>
              <th className="p-4">Cliente</th>
              <th className="p-4">Estado</th>
              <th className="p-4">Serviço</th>
              <th className="p-4">Produto</th>
              <th className="p-4">Team</th>
              <th className="p-4 text-right">Valor</th>
              <th className="p-4">Pagamento</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {sales.length === 0 && (
              <tr><td colSpan={9} className="p-6 text-black/30">Sem vendas registadas neste período.</td></tr>
            )}
            {sales.map((s) => (
              <tr
                key={s.id}
                data-testid={`sale-row-${s.id}`}
                className={`border-b border-black/5 last:border-0 transition-colors duration-300 ${
                  STATUS_ROW_COLORS[s.status] || "bg-white"
                }`}
              >
                <td className="p-4 whitespace-nowrap">{s.date}</td>
                <td className="p-4">{s.client}</td>
                <td className="p-4">
                  <select
                    data-testid={`sale-status-inline-${s.id}`}
                    value={s.status}
                    onChange={(e) => updateStatus(s.id, e.target.value)}
                    className="text-xs uppercase tracking-widest px-2 py-1.5 border bg-white border-black/20 focus:outline-none focus:border-black cursor-pointer"
                  >
                    {menus.statuses.map((st) => (
                      <option key={st} value={st}>{st}</option>
                    ))}
                    {!menus.statuses.includes(s.status) && (
                      <option value={s.status}>{s.status}</option>
                    )}
                  </select>
                </td>
                <td className="p-4">{s.service}</td>
                <td className="p-4 text-black/50">{s.product || "—"}</td>
                <td className="p-4">{s.team_member}</td>
                <td className="p-4 text-right font-medium whitespace-nowrap">{eur(s.amount)}</td>
                <td className="p-4">{s.payment}</td>
                <td className="p-4 whitespace-nowrap">
                  <button data-testid={`sale-edit-${s.id}`} onClick={() => openEdit(s)} className="p-1.5 hover:bg-black/5 mr-1" aria-label="Editar">
                    <Pencil size={15} />
                  </button>
                  <button data-testid={`sale-delete-${s.id}`} onClick={() => handleDelete(s.id)} className="p-1.5 hover:bg-black/5 text-red-600" aria-label="Eliminar">
                    <Trash2 size={15} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
